import { Component, input, output, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../../shared/services/project.service';
import { UserService } from '../../../shared/services/user.service';
import { ProjectMember, Project } from '../../../shared/models/project.model';
import { User } from '../../../shared/models/user.model';

@Component({
  selector: 'app-manage-project-members',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './manage-project-members.html',
  styleUrls: ['./manage-project-members.scss']
})
export class ManageProjectMembersComponent {
  projectId = input.required<string>();
  projectName = input<string>('');
  close = output<void>();

  private projectService = inject(ProjectService);
  private userService = inject(UserService);

  members = signal<ProjectMember[]>([]);

  /** Archived members stay linked to the project but are hidden by default. */
  showArchivedMembers = false;

  /** Members shown in the list: archived ones only when asked for. */
  get visibleMembers(): ProjectMember[] {
    return this.members().filter(m => !m.user?.isArchived || this.showArchivedMembers);
  }

  get archivedMembersCount(): number {
    return this.members().filter(m => !!m.user?.isArchived).length;
  }
  availableUsers = signal<User[]>([]);
  loading = signal(false);
  loadingUsers = signal(false);
  selectedUserId = '';
  errorMessage = signal<string>('');

  private currentUsers: User[] = [];
  private currentProject: Project | null = null;
  private loadedTenantId: string | null = null;
  private isInitialized = false; // Prevent duplicate initialization

  constructor() {
    // Single effect to load everything when projectId changes
    effect(() => {
      const id = this.projectId();
      if (id && !this.isInitialized) {
        this.isInitialized = true;
        this.initializeData();
      }
    });

    // Subscribe to projects to get tenant info and members updates
    this.projectService.projects$.subscribe(projects => {
      const id = this.projectId();
      if (id && this.isInitialized) {
        const project = projects.find(p => p.projectId === id);
        if (project) {
          // Check if tenant changed
          const tenantChanged = this.currentProject?.tenantId !== project.tenantId;

          // Convert members (always, even if empty array)
          const convertedMembers: ProjectMember[] = (project.members || []).map(m => ({
            id: m.id,
            projectId: project.projectId!,
            userId: m.userId,
            user: m.user ? {
              id: m.user.id,
              name: m.user.name,
              firstName: m.user.firstName,
              email: m.user.email
            } : undefined
          }));

          // Check if members actually changed by comparing IDs
          const currentMemberIds = this.members().map(m => m.id).sort().join(',');
          const newMemberIds = convertedMembers.map(m => m.id).sort().join(',');
          const membersChanged = currentMemberIds !== newMemberIds;

          if (membersChanged) {
            console.log('🔄 [Members] Members changed:', {
              before: this.members().length,
              after: convertedMembers.length,
              added: convertedMembers.filter(m => !this.members().find(cm => cm.id === m.id)).length,
              removed: this.members().filter(m => !convertedMembers.find(cm => cm.id === m.id)).length
            });
            this.members.set(convertedMembers);
            this.updateAvailableUsers(this.currentUsers);
          }

          this.currentProject = project;

          // Only reload tenant users if tenant changed
          if (tenantChanged && project.tenantId && project.tenantId !== this.loadedTenantId) {
            this.loadTenantUsers(project.tenantId);
          }
        }
      }
    });
  }

  // Initialize all data in parallel for better performance
  private async initializeData() {
    console.log('🚀 [Members] Initializing data...');
    this.loading.set(true);
    this.errorMessage.set('');

    try {
      // Get project from current subscription value or wait for it
      let project = this.currentProject;

      if (!project) {
        // If no project loaded yet, get it from observable
        const projects = await new Promise<Project[]>((resolve) => {
          const sub = this.projectService.projects$.subscribe(projects => {
            resolve(projects);
            sub.unsubscribe();
          });
        });
        project = projects.find(p => p.projectId === this.projectId()) ?? null;
      }

      if (!project) {
        console.warn('⚠️ [Members] Project not found');
        return;
      }

      this.currentProject = project;

      // Load members and tenant users in parallel
      const [members] = await Promise.all([
        this.projectService.getProjectMembers(this.projectId()),
        project.tenantId ? this.loadTenantUsers(project.tenantId) : Promise.resolve()
      ]);

      // Convert and set members
      const convertedMembers: ProjectMember[] = members.map(m => ({
        id: m.id,
        projectId: m.projectId,
        userId: m.userId,
        user: m.user ? {
          id: m.user.id,
          name: m.user.name,
          firstName: m.user.firstName ?? undefined,
          email: m.user.email ?? undefined
        } : undefined
      }));

      this.members.set(convertedMembers);
      this.updateAvailableUsers(this.currentUsers);

      console.log('✅ [Members] Data loaded:', {
        members: convertedMembers.length,
        availableUsers: this.currentUsers.length
      });
    } catch (e) {
      console.error('❌ [Members] Failed to initialize data', e);
      this.errorMessage.set('Failed to load data');
    } finally {
      this.loading.set(false);
    }
  }

  // Load tenant users with caching (service has built-in cache)
  private async loadTenantUsers(tenantId: string): Promise<void> {
    if (tenantId === this.loadedTenantId) {
      console.log('⏭️ [Members] Tenant users already loaded, skipping');
      return;
    }

    console.log(`📥 [Members] Loading users for tenant: ${tenantId}`);
    this.loadingUsers.set(true);

    try {
      // This method uses cache internally (60s TTL)
      const tenantUsers = await this.userService.getActiveUsersByTenantId(tenantId);
      console.log(`✅ [Members] Loaded ${tenantUsers.length} users (from cache or server)`);

      this.currentUsers = tenantUsers;
      this.loadedTenantId = tenantId;
      this.updateAvailableUsers(tenantUsers);
    } catch (e) {
      console.error('❌ [Members] Failed to load tenant users', e);
    } finally {
      this.loadingUsers.set(false);
    }
  }

  async loadMembers() {
    console.log('🔄 [Members] Reloading members...');
    this.loading.set(true);
    this.errorMessage.set('');

    try {
      const members = await this.projectService.getProjectMembers(this.projectId());

      const convertedMembers: ProjectMember[] = members.map(m => ({
        id: m.id,
        projectId: m.projectId,
        userId: m.userId,
        user: m.user ? {
          id: m.user.id,
          name: m.user.name,
          firstName: m.user.firstName ?? undefined,
          email: m.user.email ?? undefined
        } : undefined
      }));

      this.members.set(convertedMembers);
      this.updateAvailableUsers(this.currentUsers);
    } catch (e) {
      console.error('Failed to load members', e);
      this.errorMessage.set('Failed to load members');
    } finally {
      this.loading.set(false);
    }
  }

  private updateAvailableUsers(allUsers: User[]) {
    const memberUserIds = this.members().map(m => m.userId);

    // Only exclude already assigned members - tenant filtering is done server-side
    const filteredUsers = allUsers.filter(u => u.id && !memberUserIds.includes(u.id));

    this.availableUsers.set(filteredUsers);
  }

  async addMember() {
    const userId = this.selectedUserId;
    if (!userId) {
      this.errorMessage.set('Please select a user');
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    try {
      await this.projectService.addMember(this.projectId(), userId);
      // Real-time subscription will update via projects$ observable
      this.selectedUserId = '';
    } catch (e: any) {
      console.error('Failed to add member', e);
      this.errorMessage.set(e.message || 'Failed to add member');
    } finally {
      this.loading.set(false);
    }
  }

  async removeMember(projectMemberId: string) {
    if (!confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    try {
      await this.projectService.removeMember(projectMemberId);
      // Real-time subscription will update via projects$ observable
    } catch (e: any) {
      console.error('Failed to remove member', e);
      this.errorMessage.set(e.message || 'Failed to remove member');
    } finally {
      this.loading.set(false);
    }
  }

  getMemberDisplayName(member: ProjectMember): string {
    if (!member.user) return 'Unknown User';
    const name = member.user.name || '';
    const firstName = member.user.firstName || '';
    return firstName ? `${firstName} ${name}` : name;
  }

  onClose() {
    this.close.emit();
  }
}

